import { Button, Form, Input, Spin, message } from 'antd';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { verifyEmail } from '../../services/userApi';
import { brandMarkSrc } from '../../utils/brandMark';
import { isMcpAuthorizePath, trackMcpStep } from '../../utils/mcpConnectFunnel';
import { trackEvent } from '../../utils/analytics';
import './AuthForm.css';
import '../CellCog/ConsentStepper.css';

const STATUS = {
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  RESET_FORM: 'RESET_FORM',
  RESET_SUCCESS: 'RESET_SUCCESS',
};

const EmailActionHandler = () => {
  const [searchParams] = useSearchParams();
  const { currentUser, refreshUserProfile } = useAuth();
  const { isDarkMode } = useTheme();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  // Where Continue goes (CEL-3365): the verification link's continueUrl when it is on this site (the MCP consent page
  // for a signup that started there), else home. Another origin is never followed.
  const continuePath = (() => {
    try {
      const raw = searchParams.get('continueUrl');
      if (!raw) return '/';
      const u = new URL(raw, window.location.origin);
      if (u.origin !== window.location.origin) return '/';
      return `${u.pathname}${u.search}${u.hash}` || '/';
    } catch (e) {
      return '/';
    }
  })();

  useEffect(() => {
    if (!oobCode) {
      setStatus(STATUS.ERROR);
      setErrorMessage('Invalid link. No action code found.');
      return;
    }

    handleAction();
  }, [mode, oobCode]);

  // One auth_error per error screen (Nitish, Oct 10 2026): sits beside the login/signup auth_error events so we can
  // count how many people land on an expired/used email link before deciding whether to fix its copy and button.
  useEffect(() => {
    if (status !== STATUS.ERROR) return;
    trackEvent('auth_error', {
      page: 'email_link',
      method: { verifyEmail: 'verify', resetPassword: 'reset', recoverEmail: 'recover' }[mode] || 'unknown',
      error_code: /expired|already been used/.test(errorMessage) ? 'expired_or_used' : 'other',
      signed_in: !!currentUser,
    });
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async () => {
    switch (mode) {
      case 'verifyEmail':
        await handleVerifyEmail();
        break;
      case 'resetPassword':
        await handleResetPasswordInit();
        break;
      case 'recoverEmail':
        await handleRecoverEmail();
        break;
      default:
        setStatus(STATUS.ERROR);
        setErrorMessage('Unknown action type.');
    }
  };

  const handleVerifyEmail = async () => {
    try {
      await applyActionCode(auth, oobCode);

      // If user is logged in, confirm with backend and enable account
      if (currentUser) {
        try {
          // Force token refresh to get updated email_verified claim
          await currentUser.getIdToken(true);
          await verifyEmail();
          refreshUserProfile();
        } catch (apiError) {
          console.error('Backend verification failed:', apiError);
          // Still show success — email IS verified in Firebase, backend sync will catch up
        }
      }

      if (isMcpAuthorizePath(continuePath)) trackMcpStep('email_verified', { via: 'link' });
      setStatus(STATUS.SUCCESS);
    } catch (error) {
      console.error('Email verification failed:', error);
      if (error.code === 'auth/invalid-action-code') {
        setErrorMessage('This verification link has expired or has already been used.');
      } else {
        setErrorMessage('Failed to verify email. Please try again.');
      }
      setStatus(STATUS.ERROR);
    }
  };

  const handleResetPasswordInit = async () => {
    try {
      await verifyPasswordResetCode(auth, oobCode);
      setStatus(STATUS.RESET_FORM);
    } catch (error) {
      console.error('Reset code verification failed:', error);
      if (error.code === 'auth/invalid-action-code') {
        setErrorMessage('This password reset link has expired or has already been used.');
      } else {
        setErrorMessage('Invalid password reset link. Please request a new one.');
      }
      setStatus(STATUS.ERROR);
    }
  };

  const handleResetPasswordSubmit = async (values) => {
    setResetLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, values.password);
      setStatus(STATUS.RESET_SUCCESS);
    } catch (error) {
      console.error('Password reset failed:', error);
      if (error.code === 'auth/weak-password') {
        message.error('Password is too weak. Please use at least 6 characters.');
      } else {
        message.error('Failed to reset password. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleRecoverEmail = async () => {
    try {
      await applyActionCode(auth, oobCode);
      setStatus(STATUS.SUCCESS);
    } catch (error) {
      console.error('Email recovery failed:', error);
      setErrorMessage('Failed to recover email. The link may have expired.');
      setStatus(STATUS.ERROR);
    }
  };

  // The verified screen matches the terms box that follows it (480px, its padding; Nitish, Oct 2 2026).
  const wide = status === STATUS.SUCCESS && mode === 'verifyEmail';

  const renderContent = () => {
    if (status === STATUS.LOADING) {
      return (
        <div className="auth-form-header">
          <div className="auth-form-logo" style={{ marginBottom: '24px' }}>
            <Spin size="large" />
          </div>
          <h1 className="auth-form-title">Processing...</h1>
          <p className="auth-form-subtitle">Please wait while we process your request.</p>
        </div>
      );
    }

    if (status === STATUS.SUCCESS && mode === 'verifyEmail') {
      return (
        <>
          <div className="auth-form-header">
            {/* Redesigned Oct 2 2026 (Nitish): the round check, true copy (the terms are still ahead), Continue. */}
            <span className="auth-round-icon" aria-hidden="true"><Check size={28} strokeWidth={2.75} /></span>
            <h1 className="auth-form-title">Email verified</h1>
            <p className="auth-form-subtitle">
              {currentUser
                ? 'One last step: review and accept our terms to start using CellCog.'
                : 'Your email is verified. Log in to continue.'
              }
            </p>
          </div>

          <div className="auth-form-buttons" style={{ marginTop: '28px' }}>
            {currentUser ? (
              <Link to={continuePath}>
                <Button type="primary" block size="large">
                  Continue
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button type="primary" block size="large">
                  Log in
                </Button>
              </Link>
            )}
          </div>
        </>
      );
    }

    if (status === STATUS.SUCCESS && mode === 'recoverEmail') {
      return (
        <>
          <div className="auth-form-header">
            <div className="auth-form-logo" style={{ fontSize: '48px' }}>
              ✓
            </div>
            <h1 className="auth-form-title">Email recovered</h1>
            <p className="auth-form-subtitle">
              Your email address has been restored successfully.
            </p>
          </div>

          <div className="auth-form-buttons" style={{ marginTop: '32px' }}>
            <Link to="/login">
              <Button type="primary" block size="large">
                Go to Login
              </Button>
            </Link>
          </div>
        </>
      );
    }

    if (status === STATUS.RESET_FORM) {
      return (
        <>
          <div className="auth-form-header">
            <div className="auth-form-logo">
              <img
                src={brandMarkSrc(isDarkMode)}
                alt="CellCog Logo"
                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
              />
            </div>
            <h1 className="auth-form-title">Set new password</h1>
            <p className="auth-form-subtitle">
              Enter your new password below.
            </p>
          </div>

          <Form
            name="reset-password"
            onFinish={handleResetPasswordSubmit}
            layout="vertical"
            className="auth-form"
            requiredMark={false}
          >
            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter your new password' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password
                placeholder="New password (min. 6 characters)"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder="Confirm new password"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <div className="auth-form-buttons">
              <Button
                type="primary"
                htmlType="submit"
                loading={resetLoading}
                block
                size="large"
              >
                Reset Password
              </Button>
            </div>
          </Form>
        </>
      );
    }

    if (status === STATUS.RESET_SUCCESS) {
      return (
        <>
          <div className="auth-form-header">
            <div className="auth-form-logo" style={{ fontSize: '48px' }}>
              ✓
            </div>
            <h1 className="auth-form-title">Password reset</h1>
            <p className="auth-form-subtitle">
              Your password has been updated successfully. You can now log in with your new password.
            </p>
          </div>

          <div className="auth-form-buttons" style={{ marginTop: '32px' }}>
            <Link to="/login">
              <Button type="primary" block size="large">
                Go to Login
              </Button>
            </Link>
          </div>
        </>
      );
    }

    // ERROR state
    return (
      <>
        <div className="auth-form-header">
          <div className="auth-form-logo" style={{ fontSize: '48px' }}>
            ✕
          </div>
          <h1 className="auth-form-title">Something went wrong</h1>
          <p className="auth-form-subtitle">
            {errorMessage}
          </p>
        </div>

        <div className="auth-form-buttons" style={{ marginTop: '32px' }}>
          {mode === 'resetPassword' ? (
            <Link to="/forgot-password">
              <Button type="primary" block size="large">
                Request New Reset Link
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button type="primary" block size="large">
                Go to Login
              </Button>
            </Link>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="auth-page-container">
      <div className={`auth-form-wrapper${wide ? ' consent-stepper-wrapper' : ''}`}>
        <div className={`auth-form-card${wide ? ' consent-stepper-card' : ''}`}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default EmailActionHandler;
